import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, ExternalLink, Search, Upload, X } from "lucide-react";
import { apiGet, apiPatch, apiPost } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type ImportBatch = {
  id: string;
  status: string;
  parsed_count: number;
  ready_count: number;
  needs_review_count: number;
  error_count: number;
  generated_at: string | null;
};

type ImportRow = {
  id: string;
  source_entity_key: string;
  source_url: string;
  source_state: string;
  source_category: string;
  source_subcategory: string;
  name_raw: string;
  phone_raw: string | null;
  address_raw: string | null;
  review_status: string;
  review_reasons_json: string;
  duplicate_group_key: string | null;
  duplicate_count: number;
  imported_business_id: string | null;
};

type ImportSummary = {
  totalRows: number;
  needsReview: number;
  singleReason: number;
  multipleReasons: number;
  duplicateGroups: number;
  reasons: Array<{ reason: string; count: number }>;
};

const states = ["ALL", "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
const statuses = ["needs_review", "ready", "approved", "rejected", "imported"];
const reasonFilters = [
  ["ALL", "전체 사유"],
  ["duplicate_phone", "전화번호 중복"],
  ["duplicate_name_in_state", "주 내 업체명 중복"],
  ["ambiguous_name_split", "한·영문 이름 확인"],
  ["professional_credential_review", "전문 자격 확인"],
  ["missing_phone", "전화번호 없음"],
  ["missing_address", "주소 없음"],
  ["invalid_phone_format", "전화번호 형식 오류"],
] as const;

function reasons(value: string): string[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function BusinessImportsManagement() {
  const { toast } = useToast();
  const [state, setState] = useState("ALL");
  const [status, setStatus] = useState("needs_review");
  const [reason, setReason] = useState("ALL");
  const [reasonCount, setReasonCount] = useState("ALL");
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 100;
  const queryString = new URLSearchParams(
    selectedGroup
      ? {
          view: "rows",
          duplicate_group_key: selectedGroup,
          limit: String(limit),
          offset: String(offset),
        }
      : {
          view: "rows",
          status,
          limit: String(limit),
          offset: String(offset),
          ...(state === "ALL" ? {} : { state }),
          ...(reason === "ALL" ? {} : { reason }),
          ...(reasonCount === "ALL" ? {} : { reason_count: reasonCount }),
          ...(query ? { q: query } : {}),
        },
  ).toString();

  const batches = useQuery({
    queryKey: ["admin-business-import-batches"],
    queryFn: () => apiGet<{ items: ImportBatch[] }>("/api/admin/business-imports?view=batches&limit=10"),
  });
  const summary = useQuery({
    queryKey: ["admin-business-import-summary"],
    queryFn: () => apiGet<{ summary: ImportSummary }>("/api/admin/business-imports?view=summary"),
  });
  const rows = useQuery({
    queryKey: ["admin-business-import-rows", state, status, reason, reasonCount, query, selectedGroup, offset],
    queryFn: () => apiGet<{ items: ImportRow[]; total: number }>(`/api/admin/business-imports?${queryString}`),
  });

  const review = useMutation({
    mutationFn: ({ id, nextStatus }: { id: string; nextStatus: string }) =>
      apiPatch(`/api/admin/business-imports/rows/${encodeURIComponent(id)}`, { status: nextStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-business-import-rows"] });
      queryClient.invalidateQueries({ queryKey: ["admin-business-import-summary"] });
    },
    onError: (error: Error) => toast({ title: "검수 저장 실패", description: error.message, variant: "destructive" }),
  });
  const promote = useMutation({
    mutationFn: (id: string) => apiPost(`/api/admin/business-imports/rows/${encodeURIComponent(id)}/promote`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-business-import-rows"] });
      toast({ title: "업체 등록 완료", description: "미인증 무료 업체로 등록했습니다." });
    },
    onError: (error: Error) => toast({ title: "업체 등록 보류", description: error.message, variant: "destructive" }),
  });

  const latest = batches.data?.items?.[0];
  const liveSummary = summary.data?.summary;
  const items = rows.data?.items ?? [];
  const total = rows.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">업체 가져오기 검수</h2>
        <p className="text-sm text-muted-foreground">외부 수집 자료는 승인 후 한 행씩만 공개 업체로 전환됩니다.</p>
      </div>

      {latest && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">수집 완료</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{latest.parsed_count}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">자동 준비</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{latest.ready_count}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">검토 필요</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{latest.needs_review_count}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">수집 오류</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{latest.error_count}</CardContent></Card>
        </div>
      )}

      {liveSummary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">현재 검토 대기</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{liveSummary.needsReview}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">단일 사유</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{liveSummary.singleReason}</div><div className="text-xs text-muted-foreground">우선 검토 대상</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">복합 사유</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{liveSummary.multipleReasons}</CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">중복 전화 그룹</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{liveSummary.duplicateGroups}</CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>검수 대기 목록</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1"><Label>주</Label><Select value={state} onValueChange={(value) => { setState(value); setOffset(0); }}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent>{states.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>상태</Label><Select value={status} onValueChange={(value) => { setStatus(value); setOffset(0); }}><SelectTrigger className="w-44"><SelectValue /></SelectTrigger><SelectContent>{statuses.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>검토 사유</Label><Select value={reason} onValueChange={(value) => { setReason(value); setOffset(0); }}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent>{reasonFilters.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>검토 난이도</Label><Select value={reasonCount} onValueChange={(value) => { setReasonCount(value); setOffset(0); }}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ALL">전체</SelectItem><SelectItem value="single">단일 사유</SelectItem><SelectItem value="multiple">복합 사유</SelectItem></SelectContent></Select></div>
            <form className="min-w-64 flex-1 space-y-1" onSubmit={(event) => { event.preventDefault(); setQuery(searchInput.trim()); setOffset(0); }}>
              <Label htmlFor="business-import-search">업체 검색</Label>
              <div className="flex gap-2"><Input id="business-import-search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="업체명, 전화, 주소, 소스 ID" /><Button type="submit" variant="outline"><Search className="mr-1 h-4 w-4" />검색</Button></div>
            </form>
          </div>

          {selectedGroup && (
            <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-3">
              <div><div className="text-sm font-semibold">중복 전화 그룹 전체 비교</div><div className="text-xs text-muted-foreground">승인 전에 같은 전화번호를 가진 모든 수집 행을 비교하세요.</div></div>
              <Button variant="outline" size="sm" onClick={() => { setSelectedGroup(null); setOffset(0); }}>그룹 보기 종료</Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>업체</TableHead><TableHead>분류</TableHead><TableHead>연락처·주소</TableHead><TableHead>검토 사유</TableHead><TableHead className="text-right">작업</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell><div className="font-medium">{row.name_raw}</div><div className="text-xs text-muted-foreground">{row.source_state} · {row.source_entity_key}</div><a className="inline-flex items-center text-xs text-primary" href={row.source_url} target="_blank" rel="noreferrer">원본 <ExternalLink className="ml-1 h-3 w-3" /></a></TableCell>
                    <TableCell><div>{row.source_category}</div><div className="text-xs text-muted-foreground">{row.source_subcategory}</div></TableCell>
                    <TableCell><div>{row.phone_raw || "전화 없음"}</div><div className="max-w-xs text-xs text-muted-foreground">{row.address_raw || "주소 없음"}</div></TableCell>
                    <TableCell><div className="flex max-w-xs flex-wrap gap-1">{row.duplicate_count > 1 && row.duplicate_group_key && <button type="button" onClick={() => { setSelectedGroup(row.duplicate_group_key); setOffset(0); }}><Badge variant="destructive" className="cursor-pointer">중복 {row.duplicate_count}건 전체보기</Badge></button>}{reasons(row.review_reasons_json).map((reason) => <Badge key={reason} variant="outline">{reason}</Badge>)}</div></TableCell>
                    <TableCell><div className="flex justify-end gap-1">
                      {row.review_status !== "approved" && row.review_status !== "imported" && <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`${row.name_raw}을(를) 승인하시겠습니까? 중복 그룹을 먼저 확인했는지 확인하세요.`)) review.mutate({ id: row.id, nextStatus: "approved" }); }}><Check className="h-4 w-4" /></Button>}
                      {row.review_status !== "rejected" && row.review_status !== "imported" && <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`${row.name_raw}을(를) 거절하시겠습니까?`)) review.mutate({ id: row.id, nextStatus: "rejected" }); }}><X className="h-4 w-4" /></Button>}
                      {row.review_status === "approved" && <Button size="sm" onClick={() => promote.mutate(row.id)}><Upload className="mr-1 h-4 w-4" />등록</Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {!rows.isLoading && items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">해당 상태의 업체가 없습니다.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}><ChevronLeft className="mr-1 h-4 w-4" />이전</Button>
            <span className="text-sm text-muted-foreground">{total === 0 ? "0건" : `${offset + 1}–${offset + items.length} / 총 ${total}건`}</span>
            <Button variant="outline" disabled={offset + items.length >= total} onClick={() => setOffset(offset + limit)}>다음<ChevronRight className="ml-1 h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
